// Shared photo strip used by PortalJobs (active) and PortalHistory
// (completed). Signed-URL strip — gated by the
// "Portal users can read job photos" storage policy from
// 20260607004801, which checks the photo→job→company chain.
//
// Extracted from the inline PortalJobs implementation so PortalHistory
// can render the same affordance without duplication; the surface
// stays small and the policy contract stays one-place.
//
// Grouping: V2b's uploaded_by_vendor_user_id attribution surfaces
// vendor-uploaded photos in their own subgroup with a generic
// "From the technician" label. Vendor user names are NEVER exposed —
// portal RLS doesn't even let a client read vendor_users or vendors
// (no portal policy on either table; portal_jobs has no vendor
// column). The label stays generic regardless of what data leaks
// might hypothetically be reachable.
//
// Tap behavior: thumbnails open the shared in-place Lightbox (the
// staff JobPhotoStrip's Lightbox, exported and parameterized). NEVER
// target="_blank" — that tab-jacks the user out of the portal on
// mobile Safari. The Lightbox shares the same React Query cache as
// the thumbnails (key 'portal-photo-url') so opening is zero-fetch.
//
// Privacy: no vendor attribution pill ever — portal clients must not
// learn vendor identity. The grouping label "From the technician" is
// generic; the lightbox header omits the "Vendor" pill the staff
// version shows. No onDelete either — clients can't delete photos.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Image as ImageIcon } from 'lucide-react'
import { listJobPhotos, getJobPhotoSignedUrl, type JobPhoto } from '@/lib/jobPhotos'
import { Lightbox } from '@/components/JobPhotoStrip'

export function PortalPhotoStrip({ jobId }: { jobId: string }) {
  const { data: photos = [] } = useQuery({
    queryKey: ['portal-job-photos', jobId],
    queryFn: async () => {
      const { data, error } = await listJobPhotos(jobId)
      if (error) throw error
      return data
    },
  })
  // Lightbox index in the COMBINED [staff, vendor] order. null = closed.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  if (photos.length === 0) return null
  const staffPhotos  = photos.filter(p => p.uploaded_by_vendor_user_id == null)
  const vendorPhotos = photos.filter(p => p.uploaded_by_vendor_user_id != null)
  const allPhotos = [...staffPhotos, ...vendorPhotos]

  const lightbox = lightboxIndex != null && allPhotos[lightboxIndex] && (
    <Lightbox
      photos={allPhotos}
      index={lightboxIndex}
      onClose={() => setLightboxIndex(null)}
      onPrev={() => setLightboxIndex(idx => idx == null ? null : (idx - 1 + allPhotos.length) % allPhotos.length)}
      onNext={() => setLightboxIndex(idx => idx == null ? null : (idx + 1) % allPhotos.length)}
      signedUrlQueryKey="portal-photo-url"
      /* No onDelete (clients can't delete) and no showVendorAttribution
         (privacy: clients must not see vendor identity). */
    />
  )

  // Zero-vendor case: render the legacy single-row layout for pixel-
  // equivalent behavior on the overwhelming majority of jobs.
  if (vendorPhotos.length === 0) {
    return (
      <div>
        <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ImageIcon style={{ width: 11, height: 11 }} /> Photos
        </p>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
          {photos.map((p, i) => <PortalThumbnail key={p.id} photo={p} onClick={() => setLightboxIndex(i)} />)}
        </div>
        {lightbox}
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {staffPhotos.length > 0 && (
        <div>
          <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ImageIcon style={{ width: 11, height: 11 }} /> Photos
          </p>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
            {staffPhotos.map((p, i) => <PortalThumbnail key={p.id} photo={p} onClick={() => setLightboxIndex(i)} />)}
          </div>
        </div>
      )}
      <div>
        <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>From the technician</p>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
          {vendorPhotos.map((p, i) => (
            <PortalThumbnail key={p.id} photo={p}
              onClick={() => setLightboxIndex(staffPhotos.length + i)} />
          ))}
        </div>
      </div>
      {lightbox}
    </div>
  )
}

function PortalThumbnail({ photo, onClick }: { photo: JobPhoto; onClick: () => void }) {
  const { data: url } = useQuery({
    queryKey: ['portal-photo-url', photo.storage_path],
    queryFn: async () => {
      const { url, error } = await getJobPhotoSignedUrl(photo.storage_path, 300)
      if (error || !url) throw error ?? new Error('No signed URL')
      return url
    },
    staleTime: 290_000,
    gcTime: 290_000,
  })
  return (
    <button type="button" onClick={onClick}
      aria-label={photo.caption ?? 'Open photo'}
      style={{ width: 80, height: 80, flexShrink: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'var(--hover-bg)', padding: 0, cursor: 'pointer', display: 'block' }}>
      {url && <img src={url} alt={photo.caption ?? ''} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
    </button>
  )
}
